<template>
  <div class="admin-fan-photos">
    <h2>{{ $t('admin.fanPhotos.title') }}</h2>
    <p class="description">{{ $t('admin.fanPhotos.description') }}</p>

    <div class="filters">
      <div class="filter-group">
        <label for="statusFilter">{{ $t('admin.fanPhotos.filterByStatus') }}</label>
        <select id="statusFilter" v-model="statusFilter" @change="loadPhotos">
          <option value="">{{ $t('admin.fanPhotos.allPhotos') }}</option>
          <option value="0">{{ $t('admin.fanPhotos.pendingPhotos') }}</option>
          <option value="1">{{ $t('admin.fanPhotos.approvedPhotos') }}</option>
        </select>
      </div>

      <div class="filter-group">
        <label for="concertFilter">{{ $t('admin.fanPhotos.filterByConcert') }}</label>
        <select id="concertFilter" v-model="concertFilter" @change="loadPhotos">
          <option value="">{{ $t('admin.fanPhotos.allConcerts') }}</option>
          <option v-for="concert in availableConcerts" :key="concert.value" :value="concert.value">
            {{ concert.label }}
          </option>
        </select>
      </div>

      <div class="filter-group">
        <label for="sortBy">{{ $t('admin.fanPhotos.sortBy') }}</label>
        <select id="sortBy" v-model="sortBy" @change="loadPhotos">
          <option value="uploadDate">{{ $t('admin.fanPhotos.uploadDate') }}</option>
          <option value="concertDate">{{ $t('admin.fanPhotos.concertDate') }}</option>
        </select>
      </div>

      <div class="filter-group">
        <label for="sortDir">{{ $t('admin.fanPhotos.sortDirection') }}</label>
        <select id="sortDir" v-model="sortDir" @change="loadPhotos">
          <option value="DESC">{{ $t('admin.fanPhotos.newest') }}</option>
          <option value="ASC">{{ $t('admin.fanPhotos.oldest') }}</option>
        </select>
      </div>
    </div>

    <!-- Massenaktionen -->
    <div v-if="photos.length > 0" class="bulk-actions">
      <button v-if="!selectionMode" @click="startSelection" class="btn-select-mode">
        <i class="fas fa-check-square"></i>
        {{ $t('admin.fanPhotos.startSelection') }}
      </button>
      <button v-else @click="cancelSelection" class="btn-cancel-selection">
        <i class="fas fa-times"></i>
        {{ $t('admin.fanPhotos.cancelSelection') }}
      </button>
      
      <template v-if="selectedPhotos.length > 0">
        <button @click="confirmBulkDelete" class="btn-bulk-delete">
          <i class="fas fa-trash"></i>
          {{ $t('admin.fanPhotos.deleteBulk', { count: selectedPhotos.length }) }}
        </button>
        
        <button @click="bulkApprovePhotos" class="btn-bulk-approve">
          <i class="fas fa-check"></i>
          {{ $t('admin.fanPhotos.approveBulk', { count: selectedPhotos.length }) }}
        </button>
        
        <button @click="bulkUnapprovePhotos" class="btn-bulk-unapprove">
          <i class="fas fa-ban"></i>
          {{ $t('admin.fanPhotos.unapproveBulk', { count: selectedPhotos.length }) }}
        </button>
      </template>
    </div>

    <div v-if="loading" class="loading">
      <i class="fas fa-spinner fa-spin"></i> {{ $t('admin.loading') }}
    </div>

    <div v-else-if="error" class="error-message">
      <i class="fas fa-exclamation-triangle"></i> {{ $t('admin.error') }}
      <button @click="loadPhotos" class="btn-retry">
        <i class="fas fa-sync"></i> {{ $t('admin.retry') }}
      </button>
    </div>

    <div v-else-if="photos.length === 0" class="empty-state">
      <i class="fas fa-images"></i>
      <p>{{ $t('admin.fanPhotos.noPhotos') }}</p>
    </div>

    <div v-else class="photos-grid">
      <div v-for="photo in photos" :key="photo.id" class="photo-card" :class="{ 'not-approved': !photo.isApproved, 'selected': isPhotoSelected(photo.id) }">
        <!-- Checkbox für Massenauswahl -->
        <div v-if="selectionMode" class="photo-select">
          <label class="select-checkbox">
            <input 
              type="checkbox" 
              :checked="isPhotoSelected(photo.id)" 
              @change="togglePhotoSelection(photo.id)" 
            />
            <span class="checkmark"></span>
          </label>
        </div>
        
        <div class="photo-header">
          <span class="photo-id">#{{ photo.id }}</span>
          <span class="status-badge" :class="{ 'status-approved': photo.isApproved, 'status-pending': !photo.isApproved }">
            {{ photo.isApproved ? $t('admin.fanPhotos.approved') : $t('admin.fanPhotos.pending') }}
          </span>
        </div>
        
        <div class="photo-preview" @click="selectionMode ? togglePhotoSelection(photo.id) : openPhotoDetail(photo)">
          <img :src="photo.imageUrl" :alt="photo.caption || $t('admin.fanPhotos.noCaption')" />
        </div>
        
        <div class="photo-info">
          <div class="photo-caption">{{ photo.caption || $t('admin.fanPhotos.noCaption') }}</div>
          
          <div class="photo-meta">
            <div v-if="photo.concertDate" class="meta-item">
              <i class="fas fa-calendar"></i> {{ formatDate(photo.concertDate) }}
            </div>
            <div v-if="photo.concertLocation" class="meta-item">
              <i class="fas fa-map-marker-alt"></i> {{ photo.concertLocation }}
            </div>
            <div class="meta-item">
              <i class="fas fa-clock"></i> {{ formatDateTime(photo.uploadDate) }}
            </div>
          </div>
        </div>
        
        <div class="photo-actions">
          <!-- Status-Buttons prominent oben platzieren -->
          <div class="action-group approval-buttons">
            <button 
              v-if="!photo.isApproved" 
              @click="approvePhoto(photo.id)" 
              class="btn-approve" 
              :disabled="isApproving[photo.id]"
            >
              <i class="fas" :class="isApproving[photo.id] ? 'fa-spinner fa-spin' : 'fa-check'"></i>
              {{ $t('admin.fanPhotos.approve') }}
            </button>
            
            <button 
              v-else 
              @click="unapprovePhoto(photo.id)" 
              class="btn-unapprove"
              :disabled="isRejecting[photo.id]"
            >
              <i class="fas" :class="isRejecting[photo.id] ? 'fa-spinner fa-spin' : 'fa-ban'"></i>
              {{ $t('admin.fanPhotos.unapprove') }}
            </button>
          </div>
          
          <!-- Löschen-Button rechts platzieren -->
          <div class="action-group delete-button">
            <button 
              @click="confirmDeletePhoto(photo)" 
              class="btn-delete"
              :disabled="isDeleting[photo.id]"
            >
              <i class="fas" :class="isDeleting[photo.id] ? 'fa-spinner fa-spin' : 'fa-trash'"></i>
              {{ $t('admin.fanPhotos.delete') }}
            </button>
          </div>
          
          <!-- Download-Buttons am unteren Rand -->
          <div class="download-buttons">
            <a 
              :href="photo.imageUrl" 
              download 
              class="btn-card-download"
              @click.stop
            >
              <i class="fas fa-download"></i>
              {{ $t('admin.fanPhotos.download') }}
            </a>
            
            <!-- Download des Originals ohne Wasserzeichen (nur für Admins) -->
            <a 
              v-if="photo.originalImageUrl && photo.originalImageUrl !== photo.imageUrl"
              :href="`/api/download_original.php?id=${photo.id}`"
              download 
              class="btn-card-download original"
              @click.stop
            >
              <i class="fas fa-download"></i>
              {{ $t('admin.fanPhotos.downloadOriginal') }}
            </a>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal für Foto-Details -->
    <div v-if="detailPhoto" class="modal-overlay" @click="closePhotoDetail">
      <div class="modal-content photo-detail-modal" @click.stop>
        <div class="modal-header">
          <h3>{{ $t('admin.fanPhotos.photoDetails') }}</h3>
          <div class="detail-id-badge">#{{ detailPhoto.id }}</div>
          <button class="modal-close" @click="closePhotoDetail">
            <i class="fas fa-times"></i>
          </button>
        </div>
        
        <div class="modal-body">
          <div class="detail-layout">
            <div class="detail-image-container">
              <div class="detail-status-badge" :class="{ 'status-approved': detailPhoto.isApproved, 'status-pending': !detailPhoto.isApproved }">
                {{ detailPhoto.isApproved ? $t('admin.fanPhotos.approved') : $t('admin.fanPhotos.pending') }}
              </div>
              
              <img :src="detailPhoto.imageUrl" :alt="detailPhoto.caption || $t('admin.fanPhotos.noCaption')" class="detail-img" />
              
              <div class="detail-download-buttons">
                <a :href="detailPhoto.imageUrl" download class="btn-download">
                  <i class="fas fa-download"></i> {{ $t('admin.fanPhotos.download') }}
                </a>
                <!-- Download des Originals ohne Wasserzeichen im Detail-Dialog -->
                <a 
                  v-if="detailPhoto.originalImageUrl && detailPhoto.originalImageUrl !== detailPhoto.imageUrl" 
                  :href="`/api/download_original.php?id=${detailPhoto.id}`" 
                  download 
                  class="btn-download original"
                >
                  <i class="fas fa-download"></i> {{ $t('admin.fanPhotos.downloadOriginal') }}
                </a>
              </div>
            </div>
            
            <div class="detail-form">
              <div class="form-section">
                <h4 class="section-title">{{ $t('admin.fanPhotos.details') }}</h4>
                
                <div class="form-group">
                  <label for="edit-caption">{{ $t('admin.fanPhotos.caption') }}</label>
                  <textarea id="edit-caption" v-model="editForm.caption" rows="3" placeholder="Beschreibung des Fotos..."></textarea>
                </div>
                
                <div class="form-row">
                  <div class="form-group">
                    <label for="edit-date">{{ $t('admin.fanPhotos.concertDate') }}</label>
                    <input type="date" id="edit-date" v-model="editForm.concertDate" />
                  </div>
                  
                  <div class="form-group">
                    <label for="edit-location">{{ $t('admin.fanPhotos.location') }}</label>
                    <input type="text" id="edit-location" v-model="editForm.concertLocation" placeholder="Konzertort..." />
                  </div>
                </div>
              </div>
              
              <div class="form-section approval-section">
                <h4 class="section-title">{{ $t('admin.fanPhotos.approvalStatus') }}</h4>
                
                <div class="form-group form-switch">
                  <label class="switch-label">
                    <span class="switch-container">
                      <input type="checkbox" v-model="editForm.isApproved" />
                      <span class="switch-slider"></span>
                    </span>
                    <span class="switch-text">{{ $t('admin.fanPhotos.isApproved') }}</span>
                  </label>
                </div>
              </div>
              
              <div class="form-info">
                <div class="info-item">
                  <i class="fas fa-clock"></i>
                  <strong>{{ $t('admin.fanPhotos.uploadDate') }}:</strong> 
                  {{ formatDateTime(detailPhoto.uploadDate) }}
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div class="modal-footer">
          <button 
            @click="savePhotoChanges" 
            class="btn-save" 
            :disabled="isSaving"
          >
            <i class="fas" :class="isSaving ? 'fa-spinner fa-spin' : 'fa-save'"></i>
            {{ $t('admin.fanPhotos.saveChanges') }}
          </button>
          
          <button 
            @click="closePhotoDetail" 
            class="btn-cancel"
          >
            {{ $t('admin.fanPhotos.cancel') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Bestätigungsdialog für Löschen -->
    <div v-if="photoToDelete" class="modal-overlay">
      <div class="modal-content delete-confirm">
        <div class="modal-header">
          <h3>{{ $t('admin.fanPhotos.confirmDelete') }}</h3>
        </div>
        
        <div class="modal-body">
          <p>{{ $t('admin.fanPhotos.deleteWarning') }}</p>
          <div class="delete-preview">
            <img :src="photoToDelete.imageUrl" :alt="photoToDelete.caption || $t('admin.fanPhotos.noCaption')" />
          </div>
        </div>
        
        <div class="modal-footer">
          <button 
            @click="deletePhoto(photoToDelete.id)" 
            class="btn-delete-confirm" 
            :disabled="isDeleting[photoToDelete.id]"
          >
            <i class="fas" :class="isDeleting[photoToDelete.id] ? 'fa-spinner fa-spin' : 'fa-trash'"></i>
            {{ $t('admin.fanPhotos.confirmDeleteBtn') }}
          </button>
          
          <button 
            @click="cancelDelete" 
            class="btn-cancel"
          >
            {{ $t('admin.fanPhotos.cancel') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Bestätigungsdialog für Mehrfachlöschen -->
    <div v-if="showBulkDeleteConfirm" class="modal-overlay">
      <div class="modal-content delete-confirm">
        <div class="modal-header">
          <h3>{{ $t('admin.fanPhotos.confirmBulkDelete') }}</h3>
        </div>
        
        <div class="modal-body">
          <p>{{ $t('admin.fanPhotos.bulkDeleteWarning', { count: selectedPhotos.length }) }}</p>
          <div class="selected-count">
            <i class="fas fa-image"></i> {{ selectedPhotos.length }} {{ $t('admin.fanPhotos.selectedPhotos') }}
          </div>
        </div>
        
        <div class="modal-footer">
          <button 
            @click="bulkDeletePhotos" 
            class="btn-delete-confirm" 
            :disabled="isBulkDeleting"
          >
            <i class="fas" :class="isBulkDeleting ? 'fa-spinner fa-spin' : 'fa-trash'"></i>
            {{ $t('admin.fanPhotos.confirmBulkDeleteBtn') }}
          </button>
          
          <button 
            @click="cancelBulkDelete" 
            class="btn-cancel"
          >
            {{ $t('admin.fanPhotos.cancel') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AdminFanPhotos',
  data() {
    return {
      photos: [],
      loading: true,
      error: false,
      statusFilter: '',
      concertFilter: '',
      availableConcerts: [],
      loadingConcerts: false,
      sortBy: 'uploadDate',
      sortDir: 'DESC',
      detailPhoto: null,
      photoToDelete: null,
      editForm: {
        caption: '',
        concertDate: '',
        concertLocation: '',
        isApproved: false
      },
      isApproving: {},
      isRejecting: {},
      isDeleting: {},
      isSaving: false,
      selectionMode: false,
      selectedPhotos: [],
      showBulkDeleteConfirm: false,
      isBulkDeleting: false,
      isBulkApproving: false,
      isBulkRejecting: false
    }
  },
  mounted() {
    this.loadConcerts()
    this.loadPhotos()
  },
  methods: {
    async loadConcerts() {
      this.loadingConcerts = true;
      
      try {
        const response = await fetch('/api/concert_locations.php');
        const data = await response.json();
        
        if (data.success) {
          // Konzerte aus der Datum-Ort-Zuordnung extrahieren
          const concerts = [];
          
          // Für jedes Datum-Ort-Paar eine Option erstellen
          for (const [date, location] of Object.entries(data.dateLocationMap)) {
            // Formatiertes Datum für die Anzeige erstellen
            const formattedDate = this.formatDate(date);
            concerts.push({
              value: `${date}:::${location}`, // Wert als kombinierter Schlüssel aus Datum und Ort
              label: `${formattedDate} - ${location}`, // Anzeige als Datum + Ort
              date: date,
              location: location
            });
          }
          
          // Nach Datum sortieren (neueste zuerst)
          concerts.sort((a, b) => new Date(b.date) - new Date(a.date));
          
          this.availableConcerts = concerts;
        } else {
          // Bei Fehler leere Liste setzen
          this.availableConcerts = [];
        }
      } catch (error) {
        console.error('Fehler beim Laden der Konzerte:', error);
        // Leere Liste als Fallback
        this.availableConcerts = [];
      } finally {
        this.loadingConcerts = false;
      }
    },
    async loadPhotos() {
      this.loading = true
      this.error = false
      
      try {
        // Parameter für Filterung und Sortierung
        const params = new URLSearchParams()
        if (this.statusFilter !== '') {
          params.append('approved', this.statusFilter)
        }
        
        // Konzertfilter hinzufügen, wenn ausgewählt
        if (this.concertFilter) {
          // Datum und Ort aus dem kombinierten Wert extrahieren
          const [date, location] = this.concertFilter.split(':::')
          
          if (date) {
            params.append('concertDate', date)
          }
          
          if (location) {
            params.append('concertLocation', encodeURIComponent(location))
          }
        }
        
        params.append('orderBy', this.sortBy)
        params.append('orderDir', this.sortDir)
        
        const response = await fetch(`/api/admin_fan_photos.php?${params.toString()}`)
        const data = await response.json()
        
        if (data.success) {
          this.photos = data.photos
        } else {
          this.error = true
          console.error('Fehler beim Laden der Fotos:', data.error)
        }
      } catch (error) {
        this.error = true
        console.error('Netzwerkfehler:', error)
      } finally {
        this.loading = false
      }
    },
    
    formatDate(dateString) {
      if (!dateString) return ''
      
      try {
        const date = new Date(dateString)
        
        // Überprüfen, ob das Datum gültig ist
        if (isNaN(date.getTime())) {
          return ''
        }
        
        return new Intl.DateTimeFormat(this.$i18n.locale, {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        }).format(date)
      } catch (error) {
        console.error('Fehler bei der Datumsformatierung:', error, dateString)
        return ''
      }
    },
    
    formatDateTime(dateTimeString) {
      if (!dateTimeString) return ''
      
      try {
        const date = new Date(dateTimeString)
        
        // Überprüfen, ob das Datum gültig ist
        if (isNaN(date.getTime())) {
          return ''
        }
        
        return new Intl.DateTimeFormat(this.$i18n.locale, {
          year: 'numeric',
          month: 'numeric',
          day: 'numeric',
          hour: 'numeric',
          minute: 'numeric'
        }).format(date)
      } catch (error) {
        console.error('Fehler bei der Datumsformatierung:', error, dateTimeString)
        return ''
      }
    },
    
    openPhotoDetail(photo) {
      this.detailPhoto = { ...photo }
      this.editForm = {
        caption: photo.caption || '',
        concertDate: photo.concertDate || '',
        concertLocation: photo.concertLocation || '',
        isApproved: photo.isApproved === 1 || photo.isApproved === true
      }
    },
    
    closePhotoDetail() {
      this.detailPhoto = null
    },
    
    async savePhotoChanges() {
      if (!this.detailPhoto) return
      
      this.isSaving = true
      
      try {
        const response = await fetch(`/api/admin_fan_photos.php?id=${this.detailPhoto.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            caption: this.editForm.caption,
            concertDate: this.editForm.concertDate || null,
            concertLocation: this.editForm.concertLocation || '',
            isApproved: this.editForm.isApproved
          })
        })
        
        const data = await response.json()
        
        if (data.success) {
          // Foto in der Liste aktualisieren
          const index = this.photos.findIndex(p => p.id === this.detailPhoto.id)
          if (index !== -1) {
            this.photos[index] = {
              ...this.photos[index],
              caption: this.editForm.caption,
              concertDate: this.editForm.concertDate,
              concertLocation: this.editForm.concertLocation,
              isApproved: this.editForm.isApproved ? 1 : 0
            }
          }
          
          this.closePhotoDetail()
        } else {
          console.error('Fehler beim Speichern der Änderungen:', data.error)
        }
      } catch (error) {
        console.error('Netzwerkfehler:', error)
      } finally {
        this.isSaving = false
      }
    },
    
    async approvePhoto(id) {
      this.isApproving[id] = true;
      
      try {
        const response = await fetch(`/api/admin_fan_photos.php?id=${id}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            isApproved: true
          })
        })
        
        const data = await response.json()
        
        if (data.success) {
          // Status im Foto-Objekt aktualisieren
          const index = this.photos.findIndex(p => p.id === id)
          if (index !== -1) {
            this.photos[index].isApproved = 1
          }
          
          // Event zum Aktualisieren des Counters im Header emittieren
          this.$emit('photo-status-changed', { approved: true });
        } else {
          console.error('Fehler beim Genehmigen des Fotos:', data.error)
        }
      } catch (error) {
        console.error('Netzwerkfehler:', error)
      } finally {
        this.isApproving[id] = false;
      }
    },
    
    async unapprovePhoto(id) {
      this.isRejecting[id] = true;
      
      try {
        const response = await fetch(`/api/admin_fan_photos.php?id=${id}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            isApproved: false
          })
        })
        
        const data = await response.json()
        
        if (data.success) {
          // Status im Foto-Objekt aktualisieren
          const index = this.photos.findIndex(p => p.id === id)
          if (index !== -1) {
            this.photos[index].isApproved = 0
          }
          
          // Event zum Aktualisieren des Counters im Header emittieren
          this.$emit('photo-status-changed', { approved: false });
        } else {
          console.error('Fehler beim Ablehnen des Fotos:', data.error)
        }
      } catch (error) {
        console.error('Netzwerkfehler:', error)
      } finally {
        this.isRejecting[id] = false;
      }
    },
    
    confirmDeletePhoto(photo) {
      this.photoToDelete = photo
    },
    
    cancelDelete() {
      this.photoToDelete = null
    },
    
    async deletePhoto(id) {
      this.isDeleting[id] = true;
      
      try {
        const response = await fetch(`/api/admin_fan_photos.php?id=${id}`, {
          method: 'DELETE'
        })
        
        const data = await response.json()
        
        if (data.success) {
          // Foto aus der Liste entfernen
          this.photos = this.photos.filter(p => p.id !== id)
          this.photoToDelete = null
        } else {
          console.error('Fehler beim Löschen des Fotos:', data.error)
        }
      } catch (error) {
        console.error('Netzwerkfehler:', error)
      } finally {
        this.isDeleting[id] = false;
      }
    },
    
    startSelection() {
      this.selectionMode = true;
      this.selectedPhotos = [];
    },
    
    cancelSelection() {
      this.selectionMode = false;
      this.selectedPhotos = [];
    },
    
    isPhotoSelected(id) {
      return this.selectedPhotos.includes(id);
    },
    
    togglePhotoSelection(id) {
      const index = this.selectedPhotos.indexOf(id);
      if (index === -1) {
        this.selectedPhotos.push(id);
      } else {
        this.selectedPhotos.splice(index, 1);
      }
    },
    
    confirmBulkDelete() {
      if (this.selectedPhotos.length === 0) return;
      this.showBulkDeleteConfirm = true;
    },
    
    cancelBulkDelete() {
      this.showBulkDeleteConfirm = false;
    },
    
    async bulkDeletePhotos() {
      if (this.selectedPhotos.length === 0) return;
      
      this.isBulkDeleting = true;
      
      try {
        // Sequenzielle Löschung, um Server nicht zu überlasten
        let successCount = 0;
        
        for (const id of this.selectedPhotos) {
          try {
            const response = await fetch(`/api/admin_fan_photos.php?id=${id}`, {
              method: 'DELETE'
            });
            
            const data = await response.json();
            
            if (data.success) {
              successCount++;
            } else {
              console.error(`Fehler beim Löschen des Fotos (ID: ${id}):`, data.error);
            }
          } catch (error) {
            console.error(`Netzwerkfehler beim Löschen des Fotos (ID: ${id}):`, error);
          }
        }
        
        // Nur erfolgreich gelöschte Fotos aus der Liste entfernen
        if (successCount > 0) {
          this.photos = this.photos.filter(p => !this.selectedPhotos.includes(p.id));
          
          // Erfolgsmeldung anzeigen
          alert(this.$t('admin.fanPhotos.bulkDeleteSuccess', { count: successCount }));
        }
        
        // Dialog schließen und Auswahl zurücksetzen
        this.showBulkDeleteConfirm = false;
        this.selectedPhotos = [];
        this.selectionMode = false;
        
      } catch (error) {
        console.error('Fehler beim Massenentfernen von Fotos:', error);
      } finally {
        this.isBulkDeleting = false;
      }
    },
    
    async bulkApprovePhotos() {
      if (this.selectedPhotos.length === 0) return;
      
      this.isBulkApproving = true;
      let successCount = 0;
      
      try {
        for (const id of this.selectedPhotos) {
          try {
            const response = await fetch(`/api/admin_fan_photos.php?id=${id}`, {
              method: 'PATCH',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                isApproved: true
              })
            });
            
            const data = await response.json();
            
            if (data.success) {
              successCount++;
              // Status im Foto-Objekt aktualisieren
              const index = this.photos.findIndex(p => p.id === id);
              if (index !== -1) {
                this.photos[index].isApproved = 1;
              }
            }
          } catch (error) {
            console.error(`Fehler beim Freigeben des Fotos (ID: ${id}):`, error);
          }
        }
        
        if (successCount > 0) {
          // Erfolgsmeldung anzeigen
          alert(this.$t('admin.fanPhotos.bulkApproveSuccess', { count: successCount }));
          
          // Event für Massenfreigabe emittieren
          this.$emit('photo-status-changed', { approved: true, count: successCount });
        }
        
        // Auswahl zurücksetzen
        this.selectedPhotos = [];
        this.selectionMode = false;
      } catch (error) {
        console.error('Fehler beim Massenfreigeben von Fotos:', error);
      } finally {
        this.isBulkApproving = false;
      }
    },
    
    async bulkUnapprovePhotos() {
      if (this.selectedPhotos.length === 0) return;
      
      this.isBulkRejecting = true;
      let successCount = 0;
      
      try {
        for (const id of this.selectedPhotos) {
          try {
            const response = await fetch(`/api/admin_fan_photos.php?id=${id}`, {
              method: 'PATCH',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                isApproved: false
              })
            });
            
            const data = await response.json();
            
            if (data.success) {
              successCount++;
              // Status im Foto-Objekt aktualisieren
              const index = this.photos.findIndex(p => p.id === id);
              if (index !== -1) {
                this.photos[index].isApproved = 0;
              }
            }
          } catch (error) {
            console.error(`Fehler beim Ablehnen des Fotos (ID: ${id}):`, error);
          }
        }
        
        if (successCount > 0) {
          // Erfolgsmeldung anzeigen
          alert(this.$t('admin.fanPhotos.bulkUnapproveSuccess', { count: successCount }));
          
          // Event für Massenablehnung emittieren
          this.$emit('photo-status-changed', { approved: false, count: successCount });
        }
        
        // Auswahl zurücksetzen
        this.selectedPhotos = [];
        this.selectionMode = false;
      } catch (error) {
        console.error('Fehler beim Massenablehnen von Fotos:', error);
      } finally {
        this.isBulkRejecting = false;
      }
    }
  }
}
</script>

<style scoped>
/* --- Allgemeine Stile --- */
.admin-fan-photos {
  padding: 1.5rem;
  color: var(--text-color, #333);
}

h2 {
  margin-top: 0;
  margin-bottom: 1rem;
  color: var(--primary-color, #00843d);
}

.description {
  margin-bottom: 2rem;
  color: var(--text-muted, #666);
}

/* --- Filter-Bereich --- */
.filters {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 1.5rem;
  background-color: var(--white, #fff);
  padding: 1rem;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.filter-group {
  flex: 1;
  min-width: 200px;
}

.filter-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: var(--text-color, #333);
}

select {
  width: 100%;
  padding: 0.5rem;
  border: 1px solid var(--border-color, #ddd);
  border-radius: 4px;
  background-color: var(--white, #fff);
  color: var(--text-color, #333);
  min-width: 200px;
}

/* --- Lade- und Fehlerzustände --- */
.loading, .error-message, .empty-state {
  text-align: center;
  padding: 3rem;
  background-color: var(--white, #fff);
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.loading {
  color: var(--primary-color, #00843d);
  font-size: 1.2rem;
}

.error-message {
  color: var(--danger-color, #f44336);
}

.empty-state {
  color: var(--text-muted, #777);
}

.btn-retry {
  background-color: var(--primary-color, #00843d);
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  margin-top: 1rem;
  border-radius: 4px;
  cursor: pointer;
}

.empty-state i, .loading i, .error-message i {
  font-size: 3rem;
  margin-bottom: 1rem;
  display: block;
}

/* --- Foto-Grid --- */
.photos-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
}

.photo-card {
  background-color: var(--white, #fff);
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s, box-shadow 0.2s;
  display: flex;
  flex-direction: column;
  border: 1px solid var(--border-color, #e0e0e0);
}

.photo-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.12);
}

.photo-card.not-approved {
  border-left: 4px solid var(--warning-color, #ff9800);
}

.photo-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 15px;
  background-color: var(--bg-alt, #f8f8f8);
  border-bottom: 1px solid var(--border-color, #eee);
}

.photo-id {
  font-size: 14px;
  color: var(--text-muted, #888);
  font-weight: 600;
}

.status-badge {
  font-size: 13px;
  padding: 4px 10px;
  border-radius: 20px;
  font-weight: 600;
  letter-spacing: 0.3px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.status-approved {
  background-color: var(--success-bg, #e8f5e9);
  color: var(--success-color, #2e7d32);
  border: 1px solid rgba(46, 125, 50, 0.3);
}

.status-pending {
  background-color: var(--warning-bg, #fff3e0);
  color: var(--warning-color, #e65100);
  border: 1px solid rgba(230, 81, 0, 0.3);
}

.photo-preview {
  height: 200px;
  overflow: hidden;
  cursor: pointer;
  background-color: var(--bg-alt, #f8f8f8);
}

.photo-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s;
}

.photo-preview:hover img {
  transform: scale(1.05);
}

.photo-info {
  padding: 15px;
  flex-grow: 1;
}

.photo-caption {
  font-weight: 600;
  margin-bottom: 10px;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-height: 1.5;
  max-height: 3em;
  color: var(--text-color, #333);
}

.photo-meta {
  color: var(--text-muted, #777);
  font-size: 13px;
}

.meta-item {
  margin-bottom: 5px;
}

.meta-item i {
  width: 18px;
  text-align: center;
  margin-right: 5px;
}

.photo-actions {
  display: flex;
  flex-direction: column;
  padding: 15px;
  gap: 12px;
  border-top: 1px solid var(--border-color, #eee);
  background-color: var(--bg-alt, #f8f8f8);
}

.action-group {
  width: 100%;
}

.approval-buttons {
  margin-bottom: 5px;
}

.download-buttons {
  display: flex;
  gap: 8px;
  margin-top: 8px;
}

.btn-approve, .btn-unapprove {
  width: 100%;
  padding: 12px 0;
  border: none;
  border-radius: 6px;
  font-weight: 600;
  font-size: 15px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.2s;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.btn-delete, .btn-save, .btn-cancel, .btn-delete-confirm {
  width: 100%;
  padding: 10px 0;
  border: none;
  border-radius: 6px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: all 0.2s;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.btn-card-download,
.btn-download {
  padding: 10px 12px;
  border: none;
  border-radius: 6px;
  font-weight: 500;
  font-size: 13px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  transition: all 0.2s;
  text-decoration: none;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  flex: 1;
}

.btn-card-download {
  background-color: #f0f0f0;
  color: #333;
}

.btn-card-download:hover {
  background-color: #e0e0e0;
  transform: translateY(-2px);
}

.btn-card-download.original,
.btn-download.original {
  background-color: #e3edfd;
  color: #0066cc;
}

.btn-card-download.original:hover,
.btn-download.original:hover {
  background-color: #d1e3fc;
  transform: translateY(-2px);
}

.btn-approve {
  background-color: var(--success-color, #4caf50);
  color: white;
  font-size: 15px;
}

.btn-approve:hover:not(:disabled) {
  background-color: var(--success-dark, #388e3c);
  transform: translateY(-2px);
}

.btn-unapprove {
  background-color: var(--warning-color, #ff9800);
  color: white;
  font-size: 15px;
}

.btn-unapprove:hover:not(:disabled) {
  background-color: var(--warning-dark, #f57c00);
  transform: translateY(-2px);
}

.btn-delete, .btn-delete-confirm {
  background-color: var(--danger-color, #f44336);
  color: white;
}

.btn-delete:hover:not(:disabled), .btn-delete-confirm:hover:not(:disabled) {
  background-color: var(--danger-dark, #d32f2f);
  transform: translateY(-2px);
}

.btn-save {
  background-color: var(--info-color, #2196f3);
  color: white;
}

.btn-save:hover:not(:disabled) {
  background-color: var(--info-dark, #1976d2);
  transform: translateY(-2px);
}

.btn-cancel {
  background-color: var(--muted-color, #9e9e9e);
  color: white;
}

.btn-cancel:hover {
  background-color: var(--muted-dark, #757575);
  transform: translateY(-2px);
}

button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

/* -- Status-Anzeige -- */
.status-badge {
  font-size: 13px;
  padding: 4px 10px;
  border-radius: 20px;
  font-weight: 600;
  letter-spacing: 0.3px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.status-approved {
  background-color: var(--success-bg, #e8f5e9);
  color: var(--success-color, #2e7d32);
  border: 1px solid rgba(46, 125, 50, 0.3);
}

.status-pending {
  background-color: var(--warning-bg, #fff3e0);
  color: var(--warning-color, #e65100);
  border: 1px solid rgba(230, 81, 0, 0.3);
}

/* --- Modal-Stile --- */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-content {
  background-color: var(--white, #fff);
  border-radius: 8px;
  overflow: hidden;
  max-width: 90%;
  max-height: 90%;
  width: 800px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
}

.photo-detail-modal {
  width: 900px;
}

.delete-confirm {
  width: 400px;
}

.modal-header {
  padding: 18px 20px;
  background-color: var(--bg-alt, #f8f8f8);
  border-bottom: 1px solid var(--border-color, #eee);
  display: flex;
  align-items: center;
}

.modal-header h3 {
  margin: 0;
  color: var(--text-color, #333);
  flex: 1;
}

.modal-close {
  background: none;
  border: none;
  font-size: 18px;
  color: var(--text-muted, #888);
  cursor: pointer;
  transition: color 0.2s;
}

.modal-close:hover {
  color: var(--text-color, #333);
}

.modal-body {
  padding: 20px;
  overflow-y: auto;
  flex: 1;
  color: var(--text-color, #333);
}

.detail-layout {
  display: flex;
  gap: 30px;
}

.detail-image-container {
  position: relative;
  text-align: center;
  flex: 0 0 400px;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  background-color: var(--bg-alt, #f8f8f8);
}

.detail-status-badge {
  position: absolute;
  top: 15px;
  left: 15px;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;
  z-index: 2;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.status-approved {
  background-color: var(--success-bg, #e8f5e9);
  color: var(--success-color, #2e7d32);
  border: 1px solid rgba(46, 125, 50, 0.3);
}

.status-pending {
  background-color: var(--warning-bg, #fff3e0);
  color: var(--warning-color, #e65100);
  border: 1px solid rgba(230, 81, 0, 0.3);
}

.detail-img {
  max-width: 100%;
  max-height: 500px;
  display: block;
  border-radius: 8px;
  object-fit: contain;
}

.detail-download-buttons {
  position: absolute;
  bottom: 15px;
  right: 15px;
  display: flex;
  gap: 10px;
  z-index: 2;
}

.detail-form {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.detail-id-badge {
  color: var(--text-muted, #777);
  font-size: 16px;
  font-weight: 600;
  padding: 5px 12px;
  background-color: var(--bg-alt, #f8f8f8);
  border-radius: 15px;
  display: inline-block;
  margin-left: 10px;
}

.form-section {
  background-color: var(--white, #fff);
  border-radius: 10px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  margin-bottom: 20px;
}

.approval-section {
  border-left: 4px solid var(--primary-color, #00843d);
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: var(--text-color, #333);
}

.form-row {
  display: flex;
  gap: 15px;
  margin-bottom: 20px;
}

.form-row .form-group {
  flex: 1;
  margin-bottom: 0;
}

input[type="text"],
input[type="date"],
textarea {
  padding: 10px 12px;
  border: 1px solid var(--border-color, #ddd);
  border-radius: 6px;
  font-family: inherit;
  font-size: 14px;
  background-color: var(--white, #fff);
  color: var(--text-color, #333);
  width: 100%;
  transition: border-color 0.2s;
}

input[type="text"]:focus,
input[type="date"]:focus,
textarea:focus {
  border-color: var(--primary-color, #00843d);
  outline: none;
  box-shadow: 0 0 0 2px rgba(0, 132, 61, 0.1);
}

.form-switch {
  margin-top: 10px;
}

.switch-label {
  display: flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
  padding: 15px;
  border-radius: 8px;
  background-color: var(--bg-alt, #f8f8f8);
  transition: background-color 0.2s;
}

.switch-label:hover {
  background-color: var(--bg-hover, #f0f0f0);
}

.switch-container {
  display: inline-block;
  position: relative;
  min-width: 50px;
  margin-right: 20px;
}

.switch-label input {
  display: none;
}

.switch-text {
  font-size: 16px;
  font-weight: 600;
}

.switch-slider {
  display: block;
  width: 50px;
  height: 24px;
  background-color: #ccc;
  border-radius: 24px;
  position: relative;
  transition: background-color 0.3s;
}

.switch-slider::before {
  content: "";
  width: 20px;
  height: 20px;
  background-color: white;
  border-radius: 50%;
  position: absolute;
  top: 2px;
  left: 2px;
  transition: transform 0.3s;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

.switch-label input:checked + .switch-slider {
  background-color: var(--success-color, #4caf50);
}

.switch-label input:checked + .switch-slider::before {
  transform: translateX(26px);
}

.form-info {
  margin-top: 20px;
  font-size: 14px;
  color: var(--text-muted, #777);
  background-color: var(--bg-alt, #f8f8f8);
  padding: 15px;
  border-radius: 8px;
  border: 1px solid var(--border-color, #ddd);
}

.info-item {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 5px;
}

.info-item i {
  color: var(--primary-color, #00843d);
}

.modal-footer {
  padding: 20px;
  background-color: var(--bg-alt, #f8f8f8);
  border-top: 1px solid var(--border-color, #eee);
  display: flex;
  justify-content: flex-end;
  gap: 15px;
}

.btn-save, .btn-cancel {
  padding: 12px 24px;
  font-size: 16px;
  font-weight: 600;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 10px;
  transition: all 0.2s;
}

.btn-save {
  background-color: var(--primary-color, #00843d);
  color: white;
}

.btn-save:hover:not(:disabled) {
  background-color: var(--primary-dark, #006e33);
  transform: translateY(-2px);
}

.btn-cancel {
  background-color: var(--muted-color, #9e9e9e);
  color: white;
}

.btn-cancel:hover {
  background-color: var(--muted-dark, #757575);
  transform: translateY(-2px);
}

/* Responsive Design für das Detail-Modal */
@media (max-width: 768px) {
  .detail-layout {
    flex-direction: column;
  }
  
  .detail-image-container {
    flex: none;
    margin-bottom: 20px;
  }
  
  .photo-detail-modal {
    width: 95%;
  }
  
  .form-row {
    flex-direction: column;
    gap: 20px;
  }
}

/* Dark Mode spezifische Stile */
body.dark-mode .admin-fan-photos {
  --text-color: #e9ecef;
  --text-muted: #adb5bd;
  --white: #2a2e33;
  --bg-alt: #1a1d20;
  --border-color: #343a40;
  --primary-color: #4db380;
  --primary-dark: #3d8e66;
  --success-color: #66bb6a;
  --success-dark: #4caf50;
  --success-bg: rgba(102, 187, 106, 0.1);
  --warning-color: #ffa726;
  --warning-dark: #ff9800;
  --warning-bg: rgba(255, 167, 38, 0.1);
  --danger-color: #ef5350;
  --danger-dark: #e53935;
  --danger-bg: rgba(239, 83, 80, 0.1);
  --info-color: #42a5f5;
  --info-dark: #2196f3;
  --info-bg: rgba(66, 165, 245, 0.1);
  --muted-color: #78909c;
  --muted-dark: #607d8b;
}

body.dark-mode .status-approved {
  background-color: var(--success-bg);
  color: var(--success-color);
}

body.dark-mode .status-pending {
  background-color: var(--warning-bg);
  color: var(--warning-color);
}

body.dark-mode .photo-preview {
  background-color: #1c1f24;
}

body.dark-mode .modal-overlay {
  background-color: rgba(0, 0, 0, 0.8);
}

body.dark-mode input[type="text"],
body.dark-mode input[type="date"],
body.dark-mode textarea,
body.dark-mode select {
  background-color: #212529;
  border-color: #495057;
  color: #e9ecef;
}

body.dark-mode input[type="text"]:focus,
body.dark-mode input[type="date"]:focus,
body.dark-mode textarea:focus,
body.dark-mode select:focus {
  border-color: #4db380;
}

body.dark-mode .modal-close:hover {
  color: #e9ecef;
}

/* --- Stile für Massenaktionen und Auswahl --- */
.bulk-actions {
  display: flex;
  justify-content: flex-start;
  gap: 10px;
  margin-bottom: 15px;
  flex-wrap: wrap;
}

.btn-bulk-delete,
.btn-bulk-approve,
.btn-bulk-unapprove,
.btn-select-mode,
.btn-cancel-selection {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background-color: var(--button-bg, #f8f8f8);
  border: 1px solid var(--button-border, #ccc);
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.2s ease;
}

.btn-select-mode {
  background-color: var(--primary-button-bg, #4a86e8);
  color: white;
  border-color: var(--primary-button-border, #3b78e0);
}

.btn-bulk-delete {
  background-color: var(--delete-button-bg, #ff5656);
  color: white;
  border-color: var(--delete-button-border, #d43f3f);
}

.btn-bulk-approve {
  background-color: var(--success-color, #4caf50);
  color: white;
  border-color: var(--success-dark, #388e3c);
}

.btn-bulk-unapprove {
  background-color: var(--warning-color, #ff9800);
  color: white;
  border-color: var(--warning-dark, #f57c00);
}

.btn-cancel-selection {
  background-color: var(--secondary-button-bg, #f0f0f0);
  color: var(--text-color, #333);
}

.btn-select-mode:hover {
  background-color: var(--primary-button-hover-bg, #3b78e0);
}

.btn-bulk-delete:hover {
  background-color: var(--delete-button-hover-bg, #d43f3f);
}

.btn-bulk-approve:hover {
  background-color: var(--success-dark, #388e3c);
}

.btn-bulk-unapprove:hover {
  background-color: var(--warning-dark, #f57c00);
}

.btn-cancel-selection:hover {
  background-color: var(--secondary-button-hover-bg, #e5e5e5);
}

/* Stile für die Selektionsstatus */
.photo-card.selected {
  outline: 3px solid var(--selected-border, #4a86e8);
  position: relative;
}

.photo-select {
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 2;
}

.select-checkbox {
  display: block;
  position: relative;
  width: 24px;
  height: 24px;
  cursor: pointer;
}

.select-checkbox input {
  position: absolute;
  opacity: 0;
  cursor: pointer;
  height: 0;
  width: 0;
}

.checkmark {
  position: absolute;
  top: 0;
  left: 0;
  height: 24px;
  width: 24px;
  background-color: rgba(255, 255, 255, 0.8);
  border: 2px solid var(--checkbox-border, #4a86e8);
  border-radius: 4px;
}

.select-checkbox:hover input ~ .checkmark {
  background-color: #f0f0f0;
}

.select-checkbox input:checked ~ .checkmark {
  background-color: var(--checkbox-checked-bg, #4a86e8);
}

.checkmark:after {
  content: "";
  position: absolute;
  display: none;
}

.select-checkbox input:checked ~ .checkmark:after {
  display: block;
}

.select-checkbox .checkmark:after {
  left: 8px;
  top: 4px;
  width: 5px;
  height: 10px;
  border: solid white;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

/* Bulk delete confirm dialog */
.selected-count {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 15px 0;
  padding: 12px;
  background-color: #f5f5f5;
  border-radius: 4px;
  font-size: 1.1rem;
}

/* Dark mode unterstützung */
body.dark-mode .btn-select-mode {
  background-color: #4a86e8;
  border-color: #3b78e0;
  color: white;
}

body.dark-mode .btn-cancel-selection {
  background-color: #444;
  color: #eee;
  border-color: #555;
}

body.dark-mode .checkmark {
  background-color: rgba(51, 51, 51, 0.8);
  border-color: #4a86e8;
}

body.dark-mode .photo-card.selected {
  outline-color: #4a86e8;
}

body.dark-mode .selected-count {
  background-color: #333;
  color: #eee;
}
</style> 